import type { FlexProps, SelectFieldProps } from '@iheartjane/reefer';
import { Flex, Skeleton } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import { useMemo } from 'react';

import { useProductTypes } from '@jane/business-admin/data-access';
import { useProductTypeSelectOptions } from '@jane/business-admin/hooks';

// onChange is REQUIRED in categoryProps to make the subcategory field
// reset when the category is changed
type CategorySelectProps = Partial<Omit<SelectFieldProps, 'options'>> & {
  onChange: (...args: any[]) => void;
};

interface Props extends FlexProps {
  allowAllOption?: boolean;
  categoryProps: CategorySelectProps;
  isLoading?: boolean;
  subcategoryProps: Partial<Omit<SelectFieldProps, 'options'>>;
}

export const CategoryAndSubcategorySelect = ({
  allowAllOption = true,
  isLoading = false,
  categoryProps,
  subcategoryProps,
  children,
  ...flexProps
}: Props) => {
  const { watch } = useFormContext();
  const categoryWatch = watch(categoryProps.name || 'category_select');

  const { data: allProductTypes, isFetched } = useProductTypes();
  const { categoryOptions, subcategoryOptions } = useProductTypeSelectOptions(
    allProductTypes,
    allowAllOption
  );

  const sortedSubProductTypes = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(subcategoryOptions).map(([kind, arr]) => [
          kind,
          [...arr].sort((a, b) => a.label.localeCompare(b.label)),
        ])
      ),
    [subcategoryOptions]
  );

  const loadingComplete = useMemo(
    () => !isLoading && isFetched && !!categoryOptions.length,
    [isFetched, categoryOptions, isLoading]
  );

  if (!loadingComplete) {
    return (
      <Skeleton animate width="100%">
        <Flex
          gap={24}
          justifyContent="space-between"
          width="100%"
          {...flexProps}
        >
          <Skeleton.Bone height={48} width="48%" />
          <Skeleton.Bone height={48} width="48%" />
        </Flex>
      </Skeleton>
    );
  }

  return (
    <Flex gap={24} justifyContent="space-between" width="100%" {...flexProps}>
      <Form.SelectField
        loading={!loadingComplete}
        name="category_select"
        label="Category"
        options={categoryOptions}
        required
        width="100%"
        placeholder={categoryProps.placeholder}
        {...categoryProps}
      />
      <Form.SelectField
        loading={!loadingComplete}
        name="subcategory_select"
        label="Subcategory"
        placeholder={subcategoryProps.placeholder}
        options={
          categoryWatch
            ? sortedSubProductTypes[
                categoryWatch as keyof typeof sortedSubProductTypes
              ]
            : categoryProps.defaultValue
              ? sortedSubProductTypes[
                  categoryProps.defaultValue as keyof typeof sortedSubProductTypes
                ]
              : sortedSubProductTypes['flower']
        }
        required
        width="100%"
        {...subcategoryProps}
      />
      {children}
    </Flex>
  );
};
